<template>
  <div
    v-if="!(disabled && sortedAttendees.length === 0)"
    class="attendees-list"
    :class="{ compact }"
  >
    <ul class="list">
      <li v-if="owner">
        <BaseAttendee
          :attendee="owner"
          :is-deletable="false"
          :is-owner="true"
          :is-openable="isOpenable"
          @delete-attendee="deleteAttendee"
        />
      </li>
      <li v-if="isAttendable && isMeetingWithMe">
        <EventAttendanceButtons class="attendancebuttons" :event="event" />
      </li>
      <li v-for="attendee in filteredAttendees" :key="attendee.email">
        <BaseAttendee
          :attendee="attendee"
          :is-deletable="isDeletable(attendee)"
          :is-openable="isOpenable"
          @delete-attendee="deleteAttendee"
        />
      </li>
      <li v-if="!disabled">
        <ParsedInput
          ref="parsedInputEl"
          style="cursor: text"
          :completion-filter="attendeeFilter"
          placeholder="Add participant"
          :display-completions="!isParsedInputEmpty"
          compact
          no-completion-helpers
          @focus="focus"
          @blur="blur"
          @close="close"
          @escape="close"
          @submit="submitNewAttendee"
          @open="$emit('create')"
        />
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import * as _ from "lodash-es";
import {
  computed,
  ComputedRef,
  defineComponent,
  PropType,
  ref,
  toRefs,
  watch,
} from "vue";

import {
  Attendee,
  ConsoleParsingMode,
  FullEvent,
  YesNoMaybe,
} from "@ctrl/controller-rpc";

import BaseAttendee from "@/components/base/BaseAttendee.vue";
import EventAttendanceButtons from "@/components/EventAttendanceButtons.vue";
import ParsedInput, {
  CompletionFilter,
  Query,
} from "@/components/ParsedInput.vue";

export default defineComponent({
  components: {
    BaseAttendee,
    EventAttendanceButtons,
    ParsedInput,
  },
  props: {
    attendees: { type: Array as PropType<Attendee[]>, required: true },
    calendarOwnerEmail: { type: String, required: false },
    compact: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    event: { type: Object as PropType<FullEvent>, required: true },
    isAttendable: { type: Boolean, required: true },
    isMeetingWithMe: { type: Boolean, required: true },
    isOpenable: { type: Boolean, default: true },
  },
  emits: {
    create: () => true,
    "update:attendees": (_: Attendee[]) => true,
  },
  setup(props, { emit }) {
    const { attendees, calendarOwnerEmail, disabled } = toRefs(props);
    const parsedInputEl = ref<InstanceType<typeof ParsedInput>>();
    const sortedAttendees = computed<Attendee[]>(() =>
      _.sortBy(attendees.value, "organizer")
    );
    // Priority map for statuses
    const statusPriority = {
      yes: 1,
      maybe: 2,
      no: 3,
    };
    const statusPriorityFun = (status: YesNoMaybe | undefined) => {
      return status ? statusPriority[status] : 4;
    };
    const filteredAttendees = computed<Attendee[]>(() =>
      sortedAttendees.value
        .filter((attendee) => attendee.email !== calendarOwnerEmail.value)
        .sort(
          (a, b) => statusPriorityFun(a.status) - statusPriorityFun(b.status)
        )
    );
    const owner = computed<Attendee | undefined>(() =>
      sortedAttendees.value.find(
        (attendee) => attendee.email === calendarOwnerEmail.value
      )
    );
    const isDeletable = (attendee: Attendee): boolean =>
      (!attendee.organizer || sortedAttendees.value.length === 1) &&
      !disabled.value;
    const isParsedInputEmpty = computed(
      () =>
        !parsedInputEl.value ||
        (parsedInputEl.value && _.isEmpty(parsedInputEl.value.text))
    );
    const attendeeFilter: ComputedRef<CompletionFilter> = computed(
      () => (c) => {
        if (c.value.type !== "participant") return true;
        const participant = c.value.value;
        const email =
          typeof participant === "string" ? participant : participant.email;
        const attendeeEmails = attendees.value.map((a) => a.email);
        return !attendeeEmails.includes(email);
      }
    );
    const deleteAttendee = (attendeeToDelete: Attendee) => {
      const updatedAttendees = attendees.value.filter(
        (attendee) => attendee.email !== attendeeToDelete.email
      );
      emit(
        "update:attendees",
        updatedAttendees.length > 1 ? updatedAttendees : []
      );
    };
    const submitNewAttendee = (completion: Query) => {
      if (completion.kind === "update") {
        if (completion.type === "participant") {
          const completionEmail =
            typeof completion.value === "string"
              ? completion.value
              : completion.value.email;
          if (
            !_.find(
              attendees.value,
              (attendee) => attendee.email === completionEmail
            )
          ) {
            const newAttendee: Attendee = {
              email: completionEmail,
            };
            emit("update:attendees", [...attendees.value, newAttendee]);
          }
        }
        blur();
      }
    };
    const focus = () => {
      parsedInputEl.value?.fill({
        mode: "person" satisfies ConsoleParsingMode,
      });
    };

    const blur = (isInputBlurred = false) => {
      parsedInputEl.value?.clear();
      parsedInputEl.value?.fill({
        mode: "person" satisfies ConsoleParsingMode,
      });
      if (isInputBlurred) parsedInputEl.value?.queryElt?.blur();
    };
    const close = () => blur(true);
    watch(calendarOwnerEmail, (value, oldValue) => {
      if (value)
        emit(
          "update:attendees",
          attendees.value.map((attendee) =>
            attendee.email === oldValue
              ? { ...attendee, email: value }
              : attendee
          )
        );
    });
    return {
      attendeeFilter,
      blur,
      close,
      deleteAttendee,
      filteredAttendees,
      focus,
      isDeletable,
      isParsedInputEmpty,
      owner,
      parsedInputEl,
      sortedAttendees,
      submitNewAttendee,
    };
  },
});
</script>

<style lang="scss" scoped>
.attendees-list {
  cursor: default;
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  column-gap: 16px;

  ul {
    margin: 0;
    padding: 0;

    &.list {
      width: 100%;
      min-width: 0;
      display: flex;
      flex-direction: column;
      row-gap: 2px;
    }
  }

  .attendancebuttons {
    margin-inline-start: 8px;
  }

  .parsed-input :deep(p) {
    position: relative;
    border: 1px solid transparent;
    padding: 6px 8px;
    border-radius: $border-radius-m;
    @extend %font-caption-regular;

    &:hover:not(.isFocused) {
      @include themed() {
        border-color: t($grey-20);
      }
    }

    &.isFocused {
      @include themed() {
        background: t($grey-10);
        color: t($grey-70);
      }

      :deep(svg) {
        .fill {
          @include themed() {
            fill: t($grey-70);
          }
        }

        .stroke {
          @include themed() {
            stroke: t($grey-70);
          }
        }
      }
    }
  }

  .parsed-input :deep() {
    position: relative;

    .placeholder-overlay {
      @include themed() {
        color: t($grey-40);
      }
    }

    .completions {
      top: calc(100% + 4px);
      inset-inline-start: 0;
      width: calc(100% - 17px);
      position: absolute;
      padding: 4px;
      border-radius: $border-radius-l;
      @include themed() {
        background: t($bg);
        border: 1px solid t($grey-10);
      }
    }
  }

  &:not(.compact) {
    .side .side-icon {
      padding-top: 8px;
    }
  }

  &.compact {
    flex-direction: row-reverse;
    column-gap: 10px;

    .list {
      display: flex;
      flex-direction: column;
      row-gap: 4px;
    }

    li.attendee {
      padding: 4px;
    }

    .parsed-input {
      padding: 0;
      margin: 0;
      @extend %font-caption-regular;

      @include themed() {
        color: t($primary);
      }
    }
  }
}
</style>
